// API Client for Vharta Platform

import { csrfHeader } from '../csrf'
import { cookieAuthEnabled } from '../cookieAuthFlag'
import { REQUEST_TIMEOUT_MS } from './config'
import { createAuthRecovery, type AuthRecoveryController } from './authRecovery'
import {
  createStaleIdentityError,
  getIdentityGeneration,
  identitySignal,
  isIdentityStale,
  isStaleIdentityError,
} from './identityEpoch'
import { REQUEST_ID_HEADER, generateRequestId } from './requestId'
import {
  isAIInvocationEndpoint,
  isAINotEntitled,
  notEntitledScopeFromBody,
  useAIEntitlementStore,
} from '../../stores/aiEntitlementStore'

/** Marker header set ONLY on the 401-recovery probe request (see performRequest). */
const AUTH_PROBE_HEADER = 'x-vharta-auth-probe'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1'

/** RequestInit plus a per-request timeout override; stripped before the fetch call. */
type TimedRequestInit = RequestInit & { timeoutMs?: number }

/**
 * Combines the per-request timeout signal with any caller-provided signal
 * (e.g. TanStack Query's queryFn AbortSignal) so aborting either one aborts
 * the fetch. Manual merge rather than `AbortSignal.any` — this codebase
 * targets Node >=18 (package.json engines) and Vitest runs under whatever
 * local Node is installed, so this avoids depending on a combinator that
 * only landed in Node 20.3+ / newer browsers.
 */
type CombinedSignal = { signal: AbortSignal; dispose: () => void }

// combineSignals aborts the returned signal when either input aborts. The
// returned dispose() MUST be called once the request settles: without it a
// long-lived caller signal (one reused across many requests) accumulates one
// listener per request for its whole lifetime.
function combineSignals(a: AbortSignal, b: AbortSignal | null | undefined): CombinedSignal {
  if (!b) return { signal: a, dispose: () => {} }
  if (a.aborted) return { signal: a, dispose: () => {} }
  if (b.aborted) return { signal: b, dispose: () => {} }
  const controller = new AbortController()
  const onA = () => controller.abort(a.reason)
  const onB = () => controller.abort(b.reason)
  a.addEventListener('abort', onA, { once: true })
  b.addEventListener('abort', onB, { once: true })
  return {
    signal: controller.signal,
    dispose: () => {
      a.removeEventListener('abort', onA)
      b.removeEventListener('abort', onB)
    },
  }
}

// Path portion of a request URL (absolute or relative), for anchored matching.
function urlPath(url: string): string {
  try {
    return new URL(url, 'http://localhost').pathname
  } catch {
    return url
  }
}

export interface ApiError {
  status: number
  message: string
  code?: string
  details?: Record<string, unknown>
  // Per-field validation messages from the backend `field_errors` key, keyed by
  // field name. Set on 400s where the server validated a structured payload
  // field by field (e.g. connection profile fields against a connector type's
  // field_schema), so a form can mark the offending input rather than only
  // showing a banner.
  fieldErrors?: Record<string, string>
  // Structured payload from the backend `data` field (e.g. PreDeployValidationResult
  // on a 422 from POST /workflows/{id}/deploy). Callers that know the expected shape
  // for a given endpoint should narrow/cast this themselves.
  data?: unknown
  // W3-client-unify compatibility shape: mirrors axios's AxiosError.response so
  // callers migrated off services/api.ts (which threw the raw AxiosError and
  // read it via `err.response?.data?.error` / `err.response?.status`, ~94
  // importers) keep working unchanged during the migration, without forcing
  // every call site to be rewritten to the status/message/data fields above
  // in the same pass. `response.data` is the raw parsed JSON error body —
  // the same object axios would have put there. Prefer status/message/data
  // above in new code; this field exists only for migration compatibility.
  response?: {
    status: number
    data: unknown
    // Axios normalizes response headers into a plain lowercase-keyed object
    // (`err.response.headers['retry-after']`); Headers instances don't
    // support bracket access, so this is the same normalization applied to
    // fetch's Headers — see headersToRecord() below.
    headers?: Record<string, string>
  }
}

/**
 * Type guard for an error thrown by this client. apiClient throws plain
 * ApiError objects (not axios AxiosError instances), so callers that used to
 * gate on axios's `isAxiosError(err)` should gate on this instead — same
 * duck-typed shape callers already read (`err.response?.status`,
 * `err.response?.data?.error`, `err.status`, `err.message`). Duck-types on
 * `status`/`message` rather than `instanceof` since ApiError is a plain
 * object literal, not a class instance.
 */
export function isApiError(err: unknown): err is ApiError {
  return (
    !!err &&
    typeof err === 'object' &&
    typeof (err as ApiError).status === 'number' &&
    typeof (err as ApiError).message === 'string'
  )
}

/** Axios-compat: normalizes a fetch Headers instance into a plain lowercase-keyed
 * record, mirroring the shape axios puts on AxiosError.response.headers. Guards
 * against non-Headers inputs (e.g. hand-built Response mocks in tests). */
function headersToRecord(headers: unknown): Record<string, string> | undefined {
  if (!headers || typeof (headers as Headers).forEach !== 'function') return undefined
  const record: Record<string, string> = {}
  ;(headers as Headers).forEach((value, key) => {
    record[key.toLowerCase()] = value
  })
  return record
}

/**
 * Engine cluster routing: the fetch-client equivalent of the axios client's
 * request interceptor (services/api.ts) that injects X-Vharta-Cluster-ID for
 * /airflow/ and /dagster/ proxy paths. Reads ?cluster= directly from the
 * current URL — no Zustand needed, always fresh. Shared by performRequest()
 * (get/post/put/patch/delete/getByUrl) and stream().
 */
function clusterHeader(url: string): Record<string, string> {
  if (!url.includes('/airflow/') && !url.includes('/dagster/')) return {}
  const params = new URLSearchParams(window.location.search)
  const clusterId = params.get('cluster')
  return clusterId ? { 'X-Vharta-Cluster-ID': clusterId } : {}
}

export interface ApiResponse<T> {
  data: T
  message?: string
}

export interface PaginatedResponse<T> {
  data: T[]
  limit: number
  offset: number
  total?: number
}

/**
 * Exported (not just the `apiClient` singleton below) so tests can construct
 * isolated instances — each instance gets its own `authRecovery` closure
 * state (single-flight re-mint + ambiguous-401 probe guard), mirroring how
 * services/api.ts's `createApiClient()` gives each axios client its own
 * isolated guard.
 */
export class ApiClient {
  private baseUrl: string
  private token: string | null = null
  // 401 recovery (single-flight re-mint + probe-based force-logout), shared
  // implementation with the axios client — see services/api/authRecovery.ts.
  private authRecovery: AuthRecoveryController = createAuthRecovery()

  // Injected getter for platform admin impersonation context.
  // Returns a tenantId string when a platform admin has selected a specific tenant
  // to impersonate, or null in all other cases (regular users, "All Tenants" view).
  //
  // This is intentionally a getter function rather than a direct store import to
  // avoid circular dependencies (stores import apiClient) and to allow the client
  // to be instantiated before React/Zustand initialise.
  //
  // Call registerPlatformContextGetter() once during app bootstrap to wire this up.
  private getPlatformImpersonatedTenantId: (() => string | null) | null = null

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
    // Load token from storage on initialization. In cookie-only mode the token
    // is never persisted, so there is nothing to seed (in-memory only).
    this.token = cookieAuthEnabled() ? null : localStorage.getItem('vharta-auth-token')
  }

  /**
   * Register a getter that returns the platform admin's currently impersonated
   * tenant ID, or null. Call this once at app bootstrap after the Zustand stores
   * are available.
   *
   * The getter must only return a non-null value when the currently authenticated
   * user is a platform-role user who has drilled into a specific tenant.
   * It must return null for all regular tenant users — their tenant context is
   * already carried in the JWT tid claim and needs no header.
   */
  registerPlatformContextGetter(getter: () => string | null): void {
    this.getPlatformImpersonatedTenantId = getter
  }

  setToken(token: string | null) {
    this.token = token
    // WS-2 cookie-only mode: keep the token in-memory only — never write it to
    // localStorage (the XSS-exfiltration vector this cutover removes). Still
    // clear any stale key so a prior Bearer-mode token can't linger.
    if (cookieAuthEnabled()) {
      localStorage.removeItem('vharta-auth-token')
      return
    }
    if (token) {
      localStorage.setItem('vharta-auth-token', token)
    } else {
      localStorage.removeItem('vharta-auth-token')
    }
  }

  // Auth/SSO endpoints stay reachable while the tab is identity-stale (sign-out,
  // refresh, login must still work). Derived from the CONFIGURED base so it is
  // right for '/api/v1', '/api' and absolute bases alike; anchored, no substring.
  private isAuthPath(url: string): boolean {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    let basePath: string
    try {
      basePath = new URL(this.baseUrl, origin).pathname.replace(/\/+$/, '')
    } catch {
      basePath = ''
    }
    const path = urlPath(url)
    return path.startsWith(`${basePath}/auth/`) || path.startsWith(`${basePath}/sso/`)
  }

  getToken(): string | null {
    return this.token
  }

  /**
   * Returns the tenant ID to send as X-Target-Tenant-ID, or null.
   *
   * This header is only sent when a platform admin has selected a specific
   * tenant to impersonate (i.e. the platform context store has a non-null
   * selectedTenantId). For regular tenant users the JWT tid claim is
   * authoritative and no override header is needed.
   */
  private getImpersonatedTenantId(): string | null {
    if (this.getPlatformImpersonatedTenantId) {
      return this.getPlatformImpersonatedTenantId()
    }
    // Fallback: read directly from localStorage during the brief window
    // before registerPlatformContextGetter() is called (e.g. during the
    // very first render on page refresh). This avoids a blank header on
    // the first /auth/me request for platform admins.
    try {
      const stored = localStorage.getItem('vharta-platform-context')
      if (stored) {
        const parsed = JSON.parse(stored)
        return parsed.state?.selectedTenantId || null
      }
    } catch {
      // Ignore parse errors
    }
    return null
  }

  private async request<T>(
    endpoint: string,
    options: TimedRequestInit = {},
    responseType: 'json' | 'text' | 'blob' = 'json'
  ): Promise<T> {
    return this.performRequest<T>(`${this.baseUrl}${endpoint}`, options, false, responseType)
  }

  /**
   * Core request implementation shared by every method on this client:
   * endpoint-relative calls via request() (baseUrl + endpoint), and full-URL
   * calls via getByUrl() (a caller-supplied root-relative or absolute URL,
   * e.g. a backend-issued `poll_url` — see getByUrl() below). Auth headers,
   * the default timeout, and 401 recovery are identical either way.
   */
  private async performRequest<T>(
    url: string,
    options: TimedRequestInit = {},
    // Internal loop guard for the 401-recovery retry below — never set by
    // callers. Exactly one retry is ever attempted per original request
    // (mirrors the axios client's `cfg._retry` boolean in services/api.ts;
    // there is no numeric "max attempts" here because it is a single flag,
    // not a count).
    _isRetryAfterReauth = false,
    // W3-client-unify: axios-compat response parsing for the handful of
    // callers migrated off services/api.ts that read a raw text/blob body
    // (e.g. downloading a CA cert, or log tail content that may not be
    // JSON) via axios's `responseType` config — see get()'s `init` param.
    responseType: 'json' | 'text' | 'blob' = 'json'
  ): Promise<T> {
    // A FormData/Blob body must NOT get a Content-Type set here: fetch only
    // computes "multipart/form-data; boundary=..." when the Content-Type
    // header is left unset, so a hardcoded 'application/json' (or any
    // caller-set 'multipart/form-data' with no boundary) produces a body the
    // server's multipart parser can't split into parts.
    const isFormLikeBody = options.body instanceof FormData || options.body instanceof Blob

    // Issue #301: capture the identity epoch BEFORE reading the token, so a
    // response that settles after a login/tenant switch/logout is dropped.
    const requestGeneration = getIdentityGeneration()
    // A tab whose identity was changed by another tab may still READ (stale
    // but consistent) but must not WRITE under a tenant the user no longer
    // believes they are in. Auth endpoints stay open so sign-out works.
    const isSafeMethod = !options.method || ['GET', 'HEAD', 'OPTIONS'].includes(options.method.toUpperCase())
    // Cookie-only mode: the cookie already carries the OTHER tab's tenant, so even a
    // read would show the new tenant's data under the old name: block all.
    if (isIdentityStale() && (cookieAuthEnabled() || !isSafeMethod) && !this.isAuthPath(url)) {
      throw createStaleIdentityError('Your organization changed in another tab. Reload to continue.')
    }

    const headers: HeadersInit = {
      ...(isFormLikeBody ? {} : { 'Content-Type': 'application/json' }),
      [REQUEST_ID_HEADER]: generateRequestId(),
      ...options.headers,
    }

    // WS-2 cookie-only mode: drop the Bearer header and rely on the HttpOnly
    // cookie (sent via credentials:'include'). Default (flag off) is unchanged.
    if (this.token && !cookieAuthEnabled()) {
      ;(headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`
    }

    // Inject internal dev headers when pointing directly at a service (no gateway).
    const devSecret = import.meta.env.VITE_INTERNAL_SECRET
    if (devSecret) {
      ;(headers as Record<string, string>)['X-Internal-Secret'] = devSecret
      ;(headers as Record<string, string>)['X-Tenant-ID'] =
        import.meta.env.VITE_DEV_TENANT_ID || '00000000-0000-0000-0000-000000000001'
      ;(headers as Record<string, string>)['X-User-ID'] =
        import.meta.env.VITE_DEV_USER_ID || 'user-00000001'
      ;(headers as Record<string, string>)['X-Roles'] =
        import.meta.env.VITE_DEV_ROLES || 'tenant_owner,platform_admin'
    }

    // Send X-Target-Tenant-ID only for platform admin impersonation.
    // Regular tenant users rely entirely on the JWT tid claim; they must not
    // send this header or it may conflict with the backend's isolation check.
    const impersonatedTenantId = this.getImpersonatedTenantId()
    if (impersonatedTenantId) {
      ;(headers as Record<string, string>)['X-Target-Tenant-ID'] = impersonatedTenantId
    }

    // WS-2: double-submit CSRF header on mutating requests (dormant while the
    // backend CSRF middleware is in shadow + Bearer-exempt; readies the client
    // for the enforce flip). No-op on safe methods / before the cookie exists.
    Object.assign(headers as Record<string, string>, csrfHeader(options.method))

    // Engine cluster routing — see clusterHeader() above.
    Object.assign(headers as Record<string, string>, clusterHeader(url))

    // Default per-request timeout (REQUEST_TIMEOUT_MS, shared with the axios
    // client — see ./config.ts) so a hung request can't wedge the UI forever.
    // Combined with any caller-provided signal (e.g. TanStack Query's queryFn
    // AbortSignal) so an explicit caller abort still works too.
    const timeoutController = new AbortController()
    const timeoutId = setTimeout(() => timeoutController.abort(), options.timeoutMs ?? REQUEST_TIMEOUT_MS)
    const combinedCaller = combineSignals(timeoutController.signal, options.signal)
    // Also abort when the identity epoch advances (resetIdentityState).
    const combined = combineSignals(combinedCaller.signal, identitySignal())
    const signal = combined.signal

    let response: Response
    try {
      const { timeoutMs: _timeoutMs, ...fetchOptions } = options
      response = await fetch(url, {
        ...fetchOptions,
        headers,
        cache: 'no-store',
        // WS-2: send the HttpOnly auth cookies alongside the Bearer header so the
        // server can authenticate via cookie in the eventual cookie-only cutover.
        // No behavioural change on same-origin (fetch already defaults to
        // 'same-origin' credentials); this also covers cross-origin deployments,
        // where the gateway sends Access-Control-Allow-Credentials: true.
        credentials: 'include',
        signal,
      })
    } catch (fetchErr) {
      // Aborted because the identity changed: surface the typed error, not a
      // generic AbortError a caller might render or retry.
      if (requestGeneration !== getIdentityGeneration()) throw createStaleIdentityError()
      throw fetchErr
    } finally {
      clearTimeout(timeoutId)
      combined.dispose()
      combinedCaller.dispose()
    }

    // The response belongs to the OLD identity. Drop it before ANY handling:
    // in particular its 401 must not trigger re-mint or a forced logout of
    // the NEW session (handleAmbiguous401 below).
    if (requestGeneration !== getIdentityGeneration()) throw createStaleIdentityError()

    if (!response.ok) {
      // ── 401 recovery ────────────────────────────────────────────────────
      // Mirrors the axios client's flow (services/api.ts) via the shared
      // services/api/authRecovery.ts controller: single-flight re-mint the
      // access token and retry the original request ONCE; if that doesn't
      // resolve it, probe /auth/me and only force-logout if the probe itself
      // definitively 401s (protects platform-admin scope/authz 401s — which
      // are NOT session expiry — from triggering a mass logout).
      if (response.status === 401 && !devSecret) {
        // The recovery PROBE is identified by an explicit marker header set only
        // by handleAmbiguous401's probe request below — never by URL. Matching
        // on '/auth/me' excluded the app's own session check
        // (authApi.getCurrentUser) from the silent re-mint, so when the FIRST
        // expired request happened to be /auth/me the store logged the user
        // out without ever trying the cookie refresh (live 2026-09-06 18:22Z).
        const isAuthProbe = new Headers(options.headers ?? undefined).has(AUTH_PROBE_HEADER)
        // Never recurse into remint for the re-auth endpoints themselves —
        // a 401 from /auth/refresh, /sso/refresh, or /auth/switch-tenant must
        // fall straight through to the probe-based teardown below.
        const isReauthCall =
          url.includes('/sso/refresh') ||
          url.includes('/auth/refresh') ||
          url.includes('/auth/switch-tenant')
        // Sign-out calls are never recovered: re-minting or probing for the
        // session being ended would only start a second logout().
        const isLogoutCall =
          url.includes('/auth/logout') ||
          url.includes('/sso/logout') ||
          (options.method === 'DELETE' && url.includes('/sso/refresh'))
        const onLoginPage =
          typeof window !== 'undefined' && window.location.pathname.startsWith('/login')

        // Mirror api.ts: only a GENUINE expiry (WWW-Authenticate: ...
        // error="token_expired") is worth a silent re-mint. A scope/authz
        // 401 (e.g. a platform token on a tenant-only route) re-mints to the
        // same token and re-401s, so skip straight to the probe path.
        const wwwAuth = String(response.headers?.get?.('www-authenticate') ?? '').toLowerCase()
        const isExpiry = wwwAuth.includes('token_expired')

        // A stale tab (identity changed in another tab) must not re-mint: the
        // shared refresh cookie would make it ADOPT the other tab's identity.
        if (isExpiry && isIdentityStale()) throw createStaleIdentityError()

        if (isExpiry && !_isRetryAfterReauth && !isAuthProbe && !isReauthCall && !isLogoutCall && !onLoginPage) {
          const reminted = await this.authRecovery.remintAccessToken()
          // The identity may have changed while re-minting: never replay an
          // old-identity request under the new identity (#301).
          if (requestGeneration !== getIdentityGeneration()) throw createStaleIdentityError()
          if (reminted) {
            // request interceptor equivalent: this.token was already updated
            // by authStore's setToken() call inside refreshSession/
            // ssoRefreshSession/switchTenant, so the retried request picks up
            // the fresh bearer automatically.
            return this.performRequest<T>(url, options, true, responseType)
          }
          // Re-mint failed → fall through to the probe-based teardown, which
          // only logs out if /auth/me ITSELF 401s (a transient refresh
          // failure must not force-log-out a still-valid session).
        }

        if (!isAuthProbe && !isLogoutCall && !onLoginPage) {
          this.authRecovery.handleAmbiguous401(() =>
            this.performRequest('/auth/me', { headers: { [AUTH_PROBE_HEADER]: '1' } })
          )
        }
      }

      const error: ApiError = {
        status: response.status,
        message: 'An error occurred',
      }

      try {
        const errorData = await response.json()
        // Two error envelopes exist on the wire: the handlers' own
        // {success,error} and RFC 7807 Problem Details {type,title,status,
        // detail} emitted by the authorizer middleware (RequireRoleForResource
        // → apierror). W10.3's shared fixture (release_forbidden_response)
        // caught that the second one was rendered as "An error occurred".
        error.message = errorData.error || errorData.message || errorData.detail || errorData.title || error.message
        error.code = errorData.code
        error.details = errorData.details
        error.data = errorData.data
        error.fieldErrors = errorData.field_errors
        // Compatibility shape for callers migrated off the axios client — see
        // ApiError.response above.
        error.response = { status: response.status, data: errorData, headers: headersToRecord(response.headers) }
        // The body read is async: drop it if the identity changed meanwhile,
        // before it can mark entitlement state for the NEW tenant (#301).
        if (requestGeneration !== getIdentityGeneration()) throw createStaleIdentityError()
        // ADR-236: an AI call with no entitled credential. Recorded once,
        // here, so every AI surface can render the same fail-closed notice.
        // Scoped to the provider/model the body names; expires on a TTL.
        if (isAINotEntitled(response.status, errorData)) {
          useAIEntitlementStore.getState().markNotEntitled(notEntitledScopeFromBody(errorData))
        }
      } catch (bodyErr) {
        if (isStaleIdentityError(bodyErr)) throw bodyErr
        // If response is not JSON, use status text
        error.message = response.statusText
        error.response = { status: response.status, data: undefined, headers: headersToRecord(response.headers) }
      }

      throw error
    }

    // ADR-236 review F2: a model call that succeeded proves the tenant has a
    // bindable credential — the tenant-wide not-entitled notice is stale.
    if (isAIInvocationEndpoint(options.method, url)) useAIEntitlementStore.getState().clearTenantWide()

    // Handle 204 No Content
    if (response.status === 204) {
      return {} as T
    }

    // The body can take longer than the headers: re-check the epoch after it
    // is read so a body that straddled an identity change is still dropped.
    const body: unknown =
      responseType === 'text'
        ? await response.text()
        : responseType === 'blob'
          ? await response.blob()
          : await response.json()
    if (requestGeneration !== getIdentityGeneration()) throw createStaleIdentityError()
    return body as T
  }

  async get<T>(
    endpoint: string,
    params?: Record<string, unknown>,
    init?: {
      signal?: AbortSignal
      responseType?: 'json' | 'text' | 'blob'
      // #270 followups SHOULD item (view-session dedupe, ADR/doc TBD): lets a
      // caller attach a per-request header, e.g. X-View-Session, without
      // widening every GET call site's own signature. Merged into request()'s
      // header set the same way post()/put()'s `options.headers` already are.
      headers?: Record<string, string>
    }
  ): Promise<T> {
    let url = endpoint
    if (params) {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      const queryString = searchParams.toString()
      if (queryString) {
        url += `?${queryString}`
      }
    }

    return this.request<T>(
      url,
      { method: 'GET', signal: init?.signal, headers: init?.headers },
      init?.responseType
    )
  }

  /**
   * Like get(), but for a URL the caller already has in full — root-relative
   * (e.g. a backend-issued `poll_url` such as "/api/v1/tenant/approvals/{id}",
   * see internal/domain/approval/thresholds.go) or fully absolute — rather
   * than an endpoint relative to baseUrl. Goes through the SAME auth
   * headers, default timeout, and 401 recovery (single-flight re-mint +
   * retry-once + probe-based force-logout) as every other request on this
   * client, without double-prefixing `url` with baseUrl the way request()
   * would.
   */
  async getByUrl<T>(url: string, init?: { signal?: AbortSignal }): Promise<T> {
    return this.performRequest<T>(url, { method: 'GET', signal: init?.signal })
  }

  /**
   * Serializes a request body for post/put/patch. FormData (and Blob) must be
   * passed through untouched — JSON.stringify()-ing a FormData instance
   * produces "{}" and silently drops every field/file. This mirrors axios,
   * which auto-detects these types; the fetch client previously always
   * JSON-encoded, a gap only surfaced by migrating a multipart caller
   * (dagsterApi.ts's parseDbtProject) off services/api.ts.
   */
  private static serializeBody(data: unknown): BodyInit | undefined {
    if (data === undefined || data === null) return undefined
    if (data instanceof FormData || data instanceof Blob) return data
    return JSON.stringify(data)
  }

  async post<T>(
    endpoint: string,
    data?: unknown,
    options?: {
      headers?: Record<string, string>
      signal?: AbortSignal
      /** Overrides the default REQUEST_TIMEOUT_MS for a call that legitimately runs longer. */
      timeoutMs?: number
    }
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: ApiClient.serializeBody(data),
      headers: options?.headers,
      signal: options?.signal,
      timeoutMs: options?.timeoutMs,
    })
  }

  async put<T>(
    endpoint: string,
    data?: unknown,
    options?: { headers?: Record<string, string>; signal?: AbortSignal }
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: ApiClient.serializeBody(data),
      headers: options?.headers,
      signal: options?.signal,
    })
  }

  async patch<T>(
    endpoint: string,
    data?: unknown,
    options?: { headers?: Record<string, string>; signal?: AbortSignal }
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: ApiClient.serializeBody(data),
      headers: options?.headers,
      signal: options?.signal,
    })
  }

  async delete<T>(
    endpoint: string,
    options?: { data?: unknown; headers?: Record<string, string>; signal?: AbortSignal }
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: ApiClient.serializeBody(options?.data),
      headers: options?.headers,
      signal: options?.signal,
    })
  }

  /**
   * stream() returns a raw Response for SSE endpoints, reusing the centralized
   * auth + tenant header logic. Callers read the body stream themselves.
   *
   * extraHeaders allows callers to inject additional headers (e.g. X-Target-Tenant-ID
   * when the caller knows the correct tenant independently of the platform context
   * store — e.g. NLBuilderPanel opened from a chain that belongs to a specific tenant).
   * Values in extraHeaders override the defaults computed here.
   */
  async stream(
    endpoint: string,
    data?: unknown,
    signal?: AbortSignal,
    extraHeaders?: Record<string, string>
  ): Promise<Response> {
    const url = `${this.baseUrl}${endpoint}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      [REQUEST_ID_HEADER]: generateRequestId(),
    }
    if (this.token && !cookieAuthEnabled()) headers['Authorization'] = `Bearer ${this.token}`
    const impersonatedTenantId = this.getImpersonatedTenantId()
    if (impersonatedTenantId) headers['X-Target-Tenant-ID'] = impersonatedTenantId
    // Engine cluster routing — see clusterHeader() above.
    Object.assign(headers, clusterHeader(url))
    // extraHeaders last — allows callers to override the impersonated tenant if they
    // have better knowledge of the target tenant (e.g. from a chain record).
    if (extraHeaders) Object.assign(headers, extraHeaders)
    // WS-2 cookie-only mode: strip the Bearer header (cookie auth). extraHeaders
    // could have set it via caller — remove it too when the flag is on.
    if (cookieAuthEnabled()) delete headers['Authorization']
    // WS-2: SSE POST is a mutating request — carry the CSRF header too.
    Object.assign(headers, csrfHeader('POST'))

    // Issue #302 item 1: stream() had NONE of request()'s identity-epoch guard,
    // so every streaming caller (chat, NL builder, workflow generator, the
    // onboarding first-chat step) was unguarded — a tenant switch or logout
    // mid-stream left the old identity's tokens arriving into the new one's UI.
    // Routing those callers through stream() did not buy them the guard,
    // because the guard only ever existed in request(); identitySignal had
    // exactly one importer in the whole frontend. The three parts below mirror
    // request() deliberately rather than inventing a second policy.
    //
    // (1) Capture the epoch BEFORE the fetch, so a response that settles after
    //     a login/tenant switch/logout can be recognised as stale.
    const requestGeneration = getIdentityGeneration()
    // (2) Refuse outright when another tab already changed the identity. A
    //     stream is always a POST — a mutating request — so request()'s
    //     `!isSafeMethod` arm is unconditionally true here and the cookie-mode
    //     distinction collapses. Auth paths stay open so sign-out still works.
    if (isIdentityStale() && !this.isAuthPath(url)) {
      throw createStaleIdentityError('Your organization changed in another tab. Reload to continue.')
    }
    // (3) Abort in flight when the epoch advances. This matters more for a
    //     stream than for a request: a chat response arrives over seconds or
    //     minutes, so the identity can change while tokens are still landing,
    //     and without this the old tenant's output keeps rendering.
    const identityAbort = combineSignals(identitySignal(), signal)

    let response: Response
    try {
      response = await fetch(url, {
        method: 'POST',
        headers,
        body: data ? JSON.stringify(data) : undefined,
        signal: identityAbort.signal,
        credentials: 'include', // WS-2: include HttpOnly auth cookies (see request()).
      })
    } catch (fetchErr) {
      identityAbort.dispose()
      // Aborted because the identity changed: surface the TYPED error, exactly
      // as request() does. Without this the abort reaches the caller as a raw
      // DOMException named AbortError, and every streaming caller treats that
      // as a user cancel: useChatStream, useWorkflowGenerator, useNLBuilder and
      // useEditWithAI all `return` on AbortError without calling onComplete, so
      // the composer stays disabled and the turn stays "streaming" forever.
      // That is worse than the staleness this guard was added for, so the
      // mapping is not optional.
      if (requestGeneration !== getIdentityGeneration()) throw createStaleIdentityError()
      throw fetchErr
    }

    // Belt and braces for the narrow window where fetch RESOLVED just before the
    // epoch moved (the catch above covers the abort; this covers the race).
    if (requestGeneration !== getIdentityGeneration()) {
      identityAbort.dispose()
      throw createStaleIdentityError()
    }

    if (!response.ok) {
      const errData = await response
        .json()
        .catch(() => ({ error: 'Request failed' }) as Record<string, unknown>)
      if (isAINotEntitled(response.status, errData)) {
        useAIEntitlementStore.getState().markNotEntitled(notEntitledScopeFromBody(errData))
      }
      // Preserve the full body so callers can detect typed shapes (e.g.
      // APPROVALS.W4.B "approval_required" 403s) without re-parsing.
      identityAbort.dispose()
      throw {
        status: response.status,
        message:
          (typeof errData?.error === 'string' ? errData.error : undefined) ??
          `HTTP ${response.status}`,
        body: errData,
      }
    }

    // SUCCESS PATH: the body is still open and belongs to the caller, so the
    // listeners must outlive this function — disposing here would unhook the
    // identity abort that is the point of combining it. Dispose when the body
    // ends instead. combineSignals' contract says dispose() MUST be called;
    // request() does it in a finally because its body is consumed inline, and
    // stream()'s is not. Without this, every stream leaks one listener on the
    // long-lived identitySignal() (measured: 25 streams -> 25 listeners, 0
    // removals), each retaining its controller and closures until the next
    // identity change.
    // NOT disposed here, deliberately. Two options were considered:
    //   (a) read the body to detect its end — getReader() LOCKS the stream, so
    //       the caller could never read it. Rejected outright.
    //   (b) wrap it: response.body.pipeThrough(TransformStream) and return a
    //       NEW Response that disposes on flush/cancel. That changes the
    //       returned object's identity and puts a stream wrapper on every chat
    //       token, which is a real behaviour risk for a memory nit.
    // So the success path keeps its listeners. The leak is BOUNDED, which is
    // why (b) is not worth it: identityEpoch replaces the AbortController on
    // every bump, so listeners on the old controller become collectable at the
    // next identity change — the ceiling is "streams since the last login or
    // tenant switch", not unbounded growth. Error paths above do dispose, which
    // is where a stream that never hands back a body would otherwise pile up.
    return response
  }
}

// Export singleton instance
export const apiClient = new ApiClient(API_BASE_URL)

// Re-export for convenience
export { API_BASE_URL }

/**
 * Extracts a human-readable message from an error thrown by this client —
 * the fetch-client equivalent of services/api.ts's getErrorMessage() for the
 * axios client (~28 importers). performRequest() already unwraps the
 * backend's `error`/`message` JSON keys onto ApiError.message, so this is
 * mostly a direct read; the `response.data` fallback covers errors that
 * reach here un-normalized (e.g. a caller building its own error object) and
 * mirrors the axios version's `err.response?.data?.error` unwrap.
 */
export function getErrorMessage(
  err: unknown,
  fallback = 'Something went wrong. Please try again.'
): string {
  if (err && typeof err === 'object') {
    const e = err as Partial<ApiError>
    if (typeof e.message === 'string' && e.message.length > 0) return e.message
    const data = e.response?.data as { error?: unknown; message?: unknown } | undefined
    if (typeof data?.error === 'string' && data.error.length > 0) return data.error
    // Nested envelope {"error":{"code","message"}} (e.g. the data-residency gate's 403).
    const nested = data?.error as { message?: unknown } | undefined
    if (nested && typeof nested === 'object' && typeof nested.message === 'string' && nested.message.length > 0) {
      return nested.message
    }
    if (typeof data?.message === 'string' && data.message.length > 0) return data.message
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}
